import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import CalendarPicker from './CalendarPicker';
import CustomSelect from './CustomSelect';
import DocViewer from './DocViewer';
import AutocompleteInput from './AutocompleteInput';
import { useFormData } from '../hooks/useFormData';

// Cost type the backend records for a renewal of each document type
// (see COST_TYPE_FOR_DOCUMENT in backend/controllers/documentController.js).
const COST_TYPE_FOR_DOCUMENT = {
  RCA: 'RCA', CASCO: 'Casco', ITP: 'ITP', ROVINIETA: 'Rovinieta', 'ROAD ASSISTANCE': 'Road Assistance',
};
const EMPTY_COST = { amount: '', currency: 'RON', date: '', receiptRef: '' };

const DocumentForm = ({ isOpen, onClose, document, onSave, supplierName }) => {
  const toDateInputValue = (value) => {
    if (!value) return '';
    const m = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      return `${yyyy}-${mm}-${dd}`;
    }
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return '';
  };

  const [formData, setFormData] = useState({
    documentType: '',
    customDocumentType: '',
    issuer: '',
    issueDate: '',
    expiryDate: '',
    plate: '',
    notes: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  // Optional cost recorded together with the document (e.g. what the RCA renewal cost).
  const [withCost, setWithCost] = useState(false);
  const [cost, setCost] = useState(EMPTY_COST);
  const objUrlsRef = useRef([]);

  // Revoke previous object URLs to avoid memory leaks
  const revokeObjUrl = () => {
    objUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    objUrlsRef.current = [];
  };

  useEffect(() => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    setShowPreview(false);
    revokeObjUrl();
    setWithCost(false);
    setCost(EMPTY_COST);
    if (document) {
      const knownTypes = ['RCA','ITP','CASCO','Rovinieta','Road Assistance','Tachograph','Medical Certificate','Other'];
      const dt = String(document.documentType || '').trim();
      // Stored types are uppercase ("ROVINIETA", "ROAD ASSISTANCE"); match the
      // option values case-insensitively so a renewal opens on the right type
      // instead of "Other".
      const known = knownTypes.find((t) => t.toUpperCase() === dt.toUpperCase());
      setFormData({
        documentType:       known || (dt ? 'Other' : ''),
        customDocumentType: known ? '' : dt,
        issuer:     document.issuer     || '',
        issueDate:  toDateInputValue(document.issueDate),
        expiryDate: toDateInputValue(document.expiryDate),
        plate:      document.plate      || '',
        notes:      document.notes      || '',
      });
    } else {
      setFormData({
        documentType: '', customDocumentType: '',
        issuer: '', issueDate: '', expiryDate: '', plate: '', notes: '',
      });
    }
  }, [document, isOpen]);

  // Clean up object URL on unmount
  useEffect(() => () => revokeObjUrl(), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resolvedType = formData.documentType === 'Other' && formData.customDocumentType.trim()
        ? formData.customDocumentType.trim()
        : formData.documentType;
      const dataToSend = {
        documentType: resolvedType,
        issuer: formData.issuer,
        issueDate: formData.issueDate ? new Date(formData.issueDate).toISOString() : undefined,
        expiryDate: new Date(formData.expiryDate).toISOString(),
        plate: supplierName || formData.plate,
        notes: formData.notes,
      };
      if (withCost) {
        const amount = Number(cost.amount);
        if (!(amount > 0)) throw new Error('Enter the renewal cost amount, or remove the cost section.');
        dataToSend.costAmount = amount;
        dataToSend.costCurrency = cost.currency || 'RON';
        dataToSend.costDate = cost.date || formData.issueDate || new Date().toISOString().slice(0, 10);
        if (cost.receiptRef.trim()) dataToSend.costReceiptRef = cost.receiptRef.trim();
      }
      await onSave(dataToSend, selectedFiles.length ? selectedFiles : undefined);
      if (withCost) window.dispatchEvent(new CustomEvent('fleet:costs-changed'));
      onClose();
    } catch (error) {
      console.error('Error saving document:', error);
      const msg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`Error saving document: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCostChange = (e) => {
    const { name, value } = e.target;
    setCost((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const urls = files.map((file) => URL.createObjectURL(file));
    objUrlsRef.current.push(...urls);
    setSelectedFiles((prev) => [...prev, ...files]);
    setPreviewUrls((prev) => [...prev, ...urls]);
  };

  const handleRemoveFile = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    objUrlsRef.current = objUrlsRef.current.filter((u) => u !== previewUrls[index]);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const isImage = (file) => /\.(jpg|jpeg|png)$/i.test(file?.name || '');

  const isEditing = !!(document && (document._id || document.id));
  const title = isEditing ? 'Edit Auto Document' : 'Add Auto Document';
  const fd = useFormData();
  const costTypeLabel = COST_TYPE_FOR_DOCUMENT[String(formData.documentType || '').toUpperCase()] || 'Other';

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
        <div className="document-modal-intro">
          <p className="document-modal-subtitle">Upload document and set expiration date</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Document Type *</label>
            <CustomSelect
              name="documentType"
              className="form-select"
              value={formData.documentType}
              onChange={handleChange}
              required
            >
              <option value="">Select document type</option>
              <option value="RCA">RCA — Compulsory Insurance</option>
              <option value="ITP">ITP — Technical Inspection</option>
              <option value="Rovinieta">Rovinieta — Road Fee</option>
              <option value="CASCO">CASCO — Voluntary Insurance</option>
              <option value="Road Assistance">Road Assistance</option>
              <option value="Tachograph">Tachograph</option>
              <option value="Medical Certificate">Medical Certificate</option>
              <option value="Other">Other</option>
            </CustomSelect>
          </div>

          {formData.documentType === 'Other' && (
            <div className="form-group">
              <label className="form-label">Specify document type *</label>
              <input
                type="text"
                name="customDocumentType"
                className="form-input"
                value={formData.customDocumentType}
                onChange={handleChange}
                required
                placeholder="e.g. Tachograph Certificate, CNG Certificate…"
                autoFocus
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Issuer / Insurance company</label>
            <AutocompleteInput
              name="issuer" value={formData.issuer} onChange={handleChange}
              suggestions={fd.issuers} placeholder="e.g. Allianz, Omniasig, RAR"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date of issue</label>
              <CalendarPicker
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Expiration date *</label>
              <CalendarPicker
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {!isEditing && (
            supplierName ? (
              <div className="form-group">
                <label className="form-label">Supplier</label>
                <div style={{
                  padding: '8px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)',
                  borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-1)',
                }}>
                  {supplierName}
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Vehicle plate *</label>
                <AutocompleteInput
                  name="plate" value={formData.plate} onChange={handleChange}
                  suggestions={fd.plates} placeholder="e.g. B-12-XYZ"
                  required readOnly={!!(document && document.plate)}
                />
              </div>
            )
          )}

          <div className="form-group">
            <label className="form-label">Document File{selectedFiles.length > 1 ? 's' : ''}</label>
            <label className="file-drop-area">
              <input type="file" className="file-input" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={handleFileChange} />
              <div className="file-drop-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v12" />
                  <path d="M8 7l4-4 4 4" />
                  <path d="M20 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2" />
                </svg>
              </div>
              <div className="file-drop-text">Click or drag files here</div>
              <div className="file-drop-hint">PDF, JPG, PNG — max. 10MB each, multiple allowed</div>
              {selectedFiles.length > 0 && <div className="file-name">{selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected</div>}
            </label>

            {/* Inline previews after file selection */}
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                {selectedFiles.map((file, idx) => (
                  <div key={`${file.name}-${idx}`} style={{ position: 'relative' }}>
                    {isImage(file) ? (
                      <img
                        src={previewUrls[idx]}
                        alt={file.name}
                        onClick={() => { setPreviewIndex(idx); setShowPreview(true); }}
                        style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ width: '100%', height: 90, fontSize: 11, padding: 4 }}
                        onClick={() => { setPreviewIndex(idx); setShowPreview(true); }}
                      >
                        {file.name}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(idx)}
                      aria-label="Remove file"
                      style={{
                        position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%',
                        background: '#ef4444', color: '#fff', border: '2px solid #fff', fontSize: 12, lineHeight: '16px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Renewal cost — recorded as a Cost entry for this vehicle in the same save */}
          {!supplierName && (
            <div className="form-group">
              {!withCost ? (
                <button type="button" className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setWithCost(true)}>
                  + Add renewal cost
                </button>
              ) : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, background: 'var(--bg-2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Renewal cost</div>
                    <button type="button" className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={() => { setWithCost(false); setCost(EMPTY_COST); }}>Remove</button>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Amount *</label>
                      <input type="number" name="amount" className="form-input" value={cost.amount} onChange={handleCostChange} required min="0.01" step="0.01" placeholder="0.00" autoFocus />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <CustomSelect name="currency" className="form-select" value={cost.currency} onChange={handleCostChange}>
                        <option value="RON">RON</option>
                        <option value="EUR">EUR</option>
                      </CustomSelect>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Paid on</label>
                      <CalendarPicker name="date" value={cost.date || formData.issueDate} onChange={handleCostChange} placeholder="Defaults to issue date" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Receipt / Reference</label>
                      <input type="text" name="receiptRef" className="form-input" value={cost.receiptRef} onChange={handleCostChange} placeholder="e.g. INV-2026-001" />
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    Saved on the Costs page as <strong>{costTypeLabel}</strong> for {formData.plate || 'this vehicle'}.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-actions document-form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Document'}
            </button>
          </div>
        </form>
      </Modal>

      {showPreview && previewUrls[previewIndex] && (
        <DocViewer
          urls={previewUrls}
          filenames={selectedFiles.map((f) => f.name)}
          initialIndex={previewIndex}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
};

export default DocumentForm;
